/**
 * "More ▾" at the end of the Quality tabs (tabs round, finding 2): the less
 * used destinations (Routed away, Daily briefs) in one on-page drop-down, so
 * the bar keeps Triage / Waiting on others / Cases / Done up front.
 *
 * Built from page elements (a button + a menu of links), never a native
 * <select>: it opens on click, Enter, Space or ArrowDown; the arrows move
 * between entries; Escape or a click outside closes it. When the current
 * page is one of its entries it lights up and names that page ("More:
 * Routed away"). Routed away is where an undelivered send is flagged, so
 * that alarm is repeated as a red marker on "More" itself and on the entry.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import styles from './QualityTriageShell.module.css';

export interface MoreEntry {
  /** Path relative to the door ("routed", "briefs"). */
  to: string;
  label: string;
  /** Shown as a count pill beside the label; 0 / undefined shows none. */
  count?: number;
  /** A red alarm on this entry (and on "More"), e.g. "1 not delivered". */
  alarm?: string | null;
  /** How many the alarm is about: "More" shows the count, not a bare dot. */
  alarmCount?: number;
  /** Work waiting behind this entry, said in amber on "More" itself so it
   *  is seen without opening the menu ("12 to send"). */
  notice?: string | null;
}

const caret = (
  <svg
    className={styles.moreCaret}
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export function QualityMoreMenu({
  entries,
  activeTo,
}: {
  entries: MoreEntry[];
  /** The entry the reader is on, when they are on one. */
  activeTo: string | null;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const itemRefs = useRef<Array<HTMLAnchorElement | null>>([]);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (open && focusIndex !== null) itemRefs.current[focusIndex]?.focus();
  }, [open, focusIndex]);

  if (entries.length === 0) return null;
  const active = entries.find((e) => e.to === activeTo) ?? null;
  const alarms = entries.filter((e) => e.alarm);
  // Not repeated while the reader is on that entry's page.
  const notices = entries.filter((e) => e.notice && e.to !== activeTo);

  const close = (refocus: boolean) => {
    setOpen(false);
    setFocusIndex(null);
    if (refocus) buttonRef.current?.focus();
  };

  const onButtonKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setFocusIndex(0);
    } else if (e.key === 'Escape' && open) {
      e.preventDefault();
      close(false);
    }
  };

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = focusIndex ?? 0;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIndex((i + 1) % entries.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIndex((i - 1 + entries.length) % entries.length);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setFocusIndex(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setFocusIndex(entries.length - 1);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      if (e.key === 'Escape') e.preventDefault();
      close(e.key === 'Escape');
    }
  };

  const alarmTitle = alarms.map((a) => `${a.label}: ${a.alarm}`).join(' · ');

  return (
    <div className={styles.moreWrap} ref={wrapRef}>
      <button
        type="button"
        ref={buttonRef}
        className={`${styles.tab} ${styles.moreButton} ${active ? styles.tabActive : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setFocusIndex(null);
          setOpen((v) => !v);
        }}
        onKeyDown={onButtonKey}
        title={
          [alarmTitle, ...notices.map((n) => `${n.label}: ${n.notice}`)].filter(Boolean).join(' · ') || undefined
        }
      >
        {active ? `More: ${active.label}` : 'More'}
        {notices.map((n) => (
          <span key={n.to} className={styles.moreNotice} data-testid={`more-notice-${n.to}`}>
            {n.notice}
          </span>
        ))}
        {alarms.length > 0 && (
          // A count with a cross, not a bare red dot (proposal 10).
          <span className={styles.moreAlarm} data-testid="more-alarm" aria-label={alarmTitle} role="img">
            ✗ {alarms.reduce((n, a) => n + (a.alarmCount ?? 1), 0)}
          </span>
        )}
        {caret}
      </button>
      {open && (
        <div className={styles.moreMenu} role="menu" aria-label="More Quality pages" onKeyDown={onMenuKey}>
          {entries.map((e, i) => (
            <Link
              key={e.to}
              to={e.to}
              role="menuitem"
              ref={(el) => {
                itemRefs.current[i] = el;
              }}
              className={`${styles.moreItem} ${e.to === activeTo ? styles.moreItemOn : ''}`}
              aria-current={e.to === activeTo ? 'page' : undefined}
              onClick={() => close(false)}
            >
              <span className={styles.moreItemLabel}>{e.label}</span>
              {e.alarm && <span className={styles.moreItemAlarm}>{e.alarm}</span>}
              {!!e.count && <span className={`${styles.count} ${styles.countMuted}`}>{e.count}</span>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
